import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../../utils/api';
import { Asset, MaintenanceRecord, RepairRecord } from '../../../types/asset';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../../constants/permissions.constants";

type AssetCostData = {
    asset: Asset;
    maintenanceCost: number;
    repairCost: number;
    totalCost: number;
    maintenanceCount: number;
    repairCount: number;
};

const MaintenanceRepairCostReport: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const [assetsData, setAssetsData] = useState<AssetCostData[]>([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [categoryFilter, setCategoryFilter] = useState('By Asset');
    const reportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchCostData = async () => {
            try {
                setLoading(true);
                // Fetch assets with their maintenance and repair records
                const response = await api.get<Asset[]>(`/api/assets?includeMaintenance=true&includeRepairs=true`);

                const data: AssetCostData[] = response.map(asset => {
                    let maintenanceCost = 0;
                    let repairCost = 0;
                    let maintenanceCount = 0;
                    let repairCount = 0;

                    if (asset.maintenanceRecords) {
                        asset.maintenanceRecords.forEach((record: MaintenanceRecord) => {
                            if (record.cost) {
                                maintenanceCost += Number(record.cost);
                                maintenanceCount++;
                            }
                        });
                    }

                    if (asset.repairRecords) {
                        asset.repairRecords.forEach((record: RepairRecord) => {
                            if (record.cost) {
                                repairCost += Number(record.cost);
                                repairCount++;
                            }
                        });
                    }

                    return {
                        asset,
                        maintenanceCost,
                        repairCost,
                        totalCost: maintenanceCost + repairCost,
                        maintenanceCount,
                        repairCount
                    };
                }).filter(data => data.totalCost > 0 || data.maintenanceCount > 0 || data.repairCount > 0); // Only assets with service records

                // Sort by highest total cost by default
                data.sort((a, b) => b.totalCost - a.totalCost);

                setAssetsData(data);
            } catch (error) {
                console.error('Error fetching cost data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCostData();
    }, []);

    const filteredData = assetsData.filter(item => {
        const matchesSearch = item.asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.asset.tag.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesType = typeFilter === 'All Types' || item.asset.type === typeFilter;

        return matchesSearch && matchesType;
    });

    const totalServiceCost = filteredData.reduce((sum, item) => sum + item.totalCost, 0);
    const totalMaintenanceCost = filteredData.reduce((sum, item) => sum + item.maintenanceCost, 0);
    const totalRepairCost = filteredData.reduce((sum, item) => sum + item.repairCost, 0);
    const avgPerAsset = filteredData.length > 0 ? totalServiceCost / filteredData.length : 0;

    // Charts Data Prep
    const topAssetsData = filteredData.slice(0, 10).map(item => ({
        name: item.asset.name,
        tag: item.asset.tag,
        Maintenance: item.maintenanceCost,
        Repairs: item.repairCost
    }));

    const pieData = [
        { name: 'Maintenance', value: totalMaintenanceCost, color: '#3b82f6' }, // blue-500
        { name: 'Repairs', value: totalRepairCost, color: '#10b981' }, // emerald-500
    ].filter(v => v.value > 0);

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const downloadPDF = async () => {
        const element = reportRef.current;
        if (!element) return;

        try {
            setExporting(true);
            toast.info('Generating PDF...');

            const originalId = element.id;
            const tempId = `pdf-target-${Date.now()}`;
            element.id = tempId;

            const canvas = await html2canvas(element, {
                scale: 3,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: element.scrollWidth,
                imageTimeout: 0,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.getElementById(tempId);
                    if (clonedElement) {
                        const animateClasses = ['animate-in', 'fade-in', 'duration-500', 'animate-pulse'];
                        animateClasses.forEach(cls => clonedElement.classList.remove(cls));

                        const exportBtns = clonedElement.querySelectorAll('[data-export-btn]');
                        exportBtns.forEach(btn => {
                            (btn as HTMLElement).style.display = 'none';
                        });
                    }
                }
            });

            if (originalId) {
                element.id = originalId;
            } else {
                element.removeAttribute('id');
            }

            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`maintenance-repair-cost-report-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('PDF downloaded successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div ref={reportRef} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <DollarSign size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Maintenance & Repair Cost Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Track spending trends and total cost of ownership</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2" data-export-btn>
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadPDF} disabled={exporting}>
                            <Download size={16} className={exporting ? 'animate-spin' : ''} />
                            <span>{exporting ? 'Exporting...' : 'PDF'}</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-4" data-export-btn>
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search assets..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto no-scrollbar">
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-40 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={typeFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTypeFilter(e.target.value)}
                        >
                            <option>All Types</option>
                            <option>Vehicle</option>
                            <option>Plant & Equipment</option>
                            <option>Tools</option>
                            <option>IT Equipment</option>
                            <option>Mobile Devices</option>
                            <option>Lifting/Slings</option>
                            <option>Furniture</option>
                        </select>
                        <select
                            className="h-11 px-4 text-center bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full sm:w-48 appearance-none bg-no-repeat bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%20%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207.5L10%2012.5L15%207.5%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%2F%3E%3C%2Fsvg%3E')] bg-[position:right_10px_center] cursor-pointer"
                            value={categoryFilter}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategoryFilter(e.target.value)}
                        >
                            <option>By Asset</option>
                            <option>By Asset Type</option>
                            <option>By Vendor</option>
                        </select>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Service Cost</p>
                        <h3 className="text-2xl font-black text-gray-900">{formatCurrency(totalServiceCost)}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Maintenance</p>
                        <h3 className="text-2xl font-black text-blue-600">{formatCurrency(totalMaintenanceCost)}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Repairs</p>
                        <h3 className="text-2xl font-black text-orange-500">{formatCurrency(totalRepairCost)}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Avg per Asset</p>
                        <h3 className="text-2xl font-black text-gray-900">{formatCurrency(avgPerAsset)}</h3>
                    </div>
                </div>

                {/* Charts Area */}
                {!loading && filteredData.length > 0 && (
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Bar Chart */}
                        <div className="border border-gray-100 rounded-2xl p-6 bg-white shadow-sm">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Top 10 Assets by Cost</h4>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={topAssetsData} margin={{ top: 10, right: 10, left: -20, bottom: 50 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 10, fill: '#6B7280' }}
                                            axisLine={{ stroke: '#E5E7EB' }}
                                            tickLine={false}
                                            angle={-45}
                                            textAnchor="end"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 10, fill: '#6B7280' }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(value) => `$${value}`}
                                        />
                                        <RechartsTooltip
                                            cursor={{ fill: '#F3F4F6' }}
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value: number) => [formatCurrency(value)]}
                                        />
                                        <Bar dataKey="Maintenance" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="Repairs" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex items-center justify-center gap-6 mt-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                                    <span className="text-xs font-medium text-gray-500">Maintenance</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-amber-500 rounded-sm"></div>
                                    <span className="text-xs font-medium text-gray-500">Repairs</span>
                                </div>
                            </div>
                        </div>

                        {/* Pie Chart */}
                        <div className="border border-gray-100 rounded-2xl p-6 bg-white shadow-sm flex flex-col">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Cost Breakdown</h4>
                            <div className="flex-1 flex items-center justify-center relative">
                                <div className="h-64 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={0}
                                                outerRadius={90}
                                                stroke="#fff"
                                                strokeWidth={2}
                                                paddingAngle={0}
                                                dataKey="value"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip
                                                formatter={(value: number) => [formatCurrency(value)]}
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                {/* Center Labels for Pie Chart as shown in screenshot */}
                                {/* This is a bit tricky with dynamic data in Recharts standard pie, we use absolute positioning based on container approximations or customized labels */}
                                {pieData.map((entry, index) => {
                                    // Custom rough label positioning
                                    const isMaint = entry.name === 'Maintenance';
                                    return (
                                        <div
                                            key={index}
                                            className="absolute pointer-events-none"
                                            style={{
                                                top: isMaint ? '10%' : '80%',
                                                left: isMaint ? '40%' : '60%',
                                                transform: 'translate(-50%, -50%)',
                                                color: entry.color
                                            }}
                                        >
                                            <span className="text-xs font-medium">{entry.name}: {formatCurrency(entry.value)}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4 mt-6">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Assigned To</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Type</th>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Purchase Price</th>
                            )}
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Maintenance</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Repairs</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Total Service Cost</th>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Total TCO</th>
                            )}
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Service Count</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={9} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : filteredData.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                                    No cost data found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredData.map(({ asset, maintenanceCost, repairCost, totalCost, maintenanceCount, repairCount }) => {
                                const purchasePrice = Number(asset.purchasePrice) || 0;
                                const tco = purchasePrice + totalCost;

                                return (
                                    <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="min-w-[160px]">
                                                <Link
                                                    to={`/assets/${asset.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-bold text-gray-900 text-[13px] hover:text-blue-600 hover:underline transition-colors block"
                                                >
                                                    {asset.name}
                                                </Link>
                                                <p className="text-[11px] font-medium text-gray-400 font-mono tracking-wider mt-0.5">{asset.tag}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700">
                                            {asset.assignedToUser?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[11px] font-bold text-gray-600 border border-gray-200 rounded-md px-2.5 py-1 whitespace-nowrap bg-white">{asset.type}</span>
                                        </td>
                                        {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                            <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap font-mono">
                                                {formatCurrency(purchasePrice)}
                                            </td>
                                        )}
                                        <td className="px-6 py-4 text-[13px] font-medium text-blue-600 whitespace-nowrap font-mono">
                                            {formatCurrency(maintenanceCost)}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-red-500 whitespace-nowrap font-mono">
                                            {formatCurrency(repairCost)}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-bold text-gray-900 whitespace-nowrap font-mono">
                                            {formatCurrency(totalCost)}
                                        </td>
                                        {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                            <td className="px-6 py-4 text-[13px] font-black text-gray-900 whitespace-nowrap font-mono">
                                                {formatCurrency(tco)}
                                            </td>
                                        )}
                                        <td className="px-6 py-4 text-[11px] text-gray-500 whitespace-nowrap">
                                            <p>{maintenanceCount} maintenance</p>
                                            <p>{repairCount} repairs</p>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MaintenanceRepairCostReport;
